import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { diaryApi } from '../lib/api';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
    ClipboardCheck,
    RefreshCw,
    Plus,
    CalendarIcon,
    CheckCircle2,
    Clock,
    FileSignature,
    ChevronDown,
    ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { getTodayDate, formatDate, formatDateTime, capitalize, cn } from '../lib/utils';
import { format } from 'date-fns';

const checklistTypes = [
    { id: 'opening', label: 'Opening Check', icon: '🌅' },
    { id: 'closing', label: 'Closing Check', icon: '🌙' },
    { id: 'cleaning', label: 'Cleaning Schedule', icon: '🧹' }
];

const DailyDiary = () => {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || getTodayDate());
    const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
    const [newNotes, setNewNotes] = useState('');

    useEffect(() => {
        loadEntries();
    }, [selectedDate]);

    const loadEntries = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedDate) params.date = selectedDate;
            const response = await diaryApi.getAll(params);
            setEntries(response.data);
        } catch (error) {
            console.error('Failed to load diary entries:', error);
            toast.error('Failed to load diary entries');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEntry = async () => {
        if (!selectedType) {
            toast.error('Please select a checklist type');
            return;
        }

        try {
            const response = await diaryApi.create({
                date: selectedDate,
                checklist_type: selectedType,
                notes: newNotes
            });
            setEntries([response.data, ...entries]);
            setSelectedEntry(response.data);
            setCreateDialogOpen(false);
            setSelectedType('');
            setNewNotes('');
            toast.success('Checklist created successfully');
        } catch (error) {
            if (error.response?.data?.detail?.includes('already exists')) {
                toast.error('A checklist of this type already exists for this date');
            } else {
                toast.error('Failed to create checklist');
            }
        }
    };

    const handleItemToggle = async (itemId, completed) => {
        if (!selectedEntry) return;

        try {
            const response = await diaryApi.updateItem(selectedEntry.id, {
                item_id: itemId,
                is_completed: completed
            });
            setSelectedEntry(response.data);
            // Update in entries list too
            setEntries(entries.map(e => e.id === response.data.id ? response.data : e));
            toast.success(completed ? 'Item completed' : 'Item unchecked');
        } catch (error) {
            console.error('Failed to update item:', error);
            toast.error('Failed to update item');
        }
    };

    const handleSignOff = async () => {
        if (!selectedEntry) return;

        try {
            const response = await diaryApi.signOff(selectedEntry.id);
            setSelectedEntry(response.data);
            setEntries(entries.map(e => e.id === response.data.id ? response.data : e));
            toast.success('Checklist signed off successfully');
        } catch (error) {
            if (error.response?.data?.detail?.includes('incomplete')) {
                toast.error('Cannot sign off incomplete checklist');
            } else {
                toast.error('Failed to sign off checklist');
            }
        }
    };

    const handleDateSelect = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        setSelectedDate(dateStr);
        searchParams.set('date', dateStr);
        setSearchParams(searchParams);
        setSelectedEntry(null);
    };

    if (loading && !entries.length) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    const todayEntries = entries.filter(e => e.date === selectedDate);

    return (
        <Layout>
            <div className="space-y-6" data-testid="daily-diary-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Daily Diary</h1>
                        <p className="text-slate-400 mt-1">
                            Manage your daily food safety checklists
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                    data-testid="date-picker-trigger"
                                >
                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                    {formatDate(selectedDate)}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="end">
                                <Calendar
                                    mode="single"
                                    selected={new Date(selectedDate)}
                                    onSelect={handleDateSelect}
                                    className="text-slate-200"
                                />
                            </PopoverContent>
                        </Popover>
                        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="brand-btn" data-testid="create-checklist-button">
                                    <Plus className="h-4 w-4 mr-2" />
                                    New Checklist
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800">
                                <DialogHeader>
                                    <DialogTitle className="text-slate-100">Create New Checklist</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Date</Label>
                                        <p className="text-slate-400">{formatDate(selectedDate)}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Checklist Type</Label>
                                        <Select value={selectedType} onValueChange={setSelectedType}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="checklist-type-select">
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {checklistTypes.map(type => (
                                                    <SelectItem key={type.id} value={type.id} className="text-slate-200 focus:bg-slate-800">
                                                        {type.icon} {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Notes (optional)</Label>
                                        <Textarea
                                            value={newNotes}
                                            onChange={(e) => setNewNotes(e.target.value)}
                                            placeholder="Any additional notes..."
                                            className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                                            data-testid="checklist-notes-input"
                                        />
                                    </div>
                                    <Button
                                        onClick={handleCreateEntry}
                                        className="w-full brand-btn"
                                        data-testid="create-checklist-submit"
                                    >
                                        Create Checklist
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Entries List */}
                    <div className="lg:col-span-1 space-y-4">
                        <h2 className="text-lg font-semibold text-slate-200">
                            Checklists for {formatDate(selectedDate)}
                        </h2>
                        
                        {todayEntries.length > 0 ? (
                            <div className="space-y-3">
                                {todayEntries.map(entry => (
                                    <Card
                                        key={entry.id}
                                        className={cn(
                                            'bg-slate-900 border-slate-800 cursor-pointer transition-all',
                                            selectedEntry?.id === entry.id ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)]/20' : 'hover:border-slate-700'
                                        )}
                                        onClick={() => setSelectedEntry(entry)}
                                        data-testid={`diary-entry-${entry.id}`}
                                    >
                                        <CardContent className="py-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={cn(
                                                        'h-10 w-10 rounded-lg flex items-center justify-center',
                                                        entry.status === 'completed' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                                                    )}>
                                                        {entry.status === 'completed' ? (
                                                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                                                        ) : (
                                                            <Clock className="h-5 w-5 text-amber-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-200">{capitalize(entry.checklist_type)}</p>
                                                        <p className="text-xs text-slate-500">
                                                            {entry.items.filter(i => i.is_completed).length}/{entry.items.length} items
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <StatusBadge status={entry.status} size="sm" />
                                                    <ChevronRight className="h-4 w-4 text-slate-600" />
                                                </div>
                                            </div>
                                            {entry.signed_off_by && (
                                                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                                                    <FileSignature className="h-3 w-3" />
                                                    Signed off by {entry.signed_off_by}
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="py-8">
                                    <div className="empty-state">
                                        <ClipboardCheck className="empty-state-icon" />
                                        <p className="empty-state-title">No checklists</p>
                                        <p className="empty-state-description">
                                            Create a checklist for this date
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Entry Detail */}
                    <div className="lg:col-span-2">
                        {selectedEntry ? (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardHeader className="border-b border-slate-800">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="text-xl text-slate-100">
                                                {capitalize(selectedEntry.checklist_type)} Checklist
                                            </CardTitle>
                                            <CardDescription className="text-slate-400">
                                                {formatDate(selectedEntry.date)} • Created {formatDateTime(selectedEntry.created_at)}
                                            </CardDescription>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <StatusBadge status={selectedEntry.status} />
                                            {isManager && selectedEntry.status === 'completed' && !selectedEntry.signed_off_by && (
                                                <Button
                                                    onClick={handleSignOff}
                                                    className="bg-emerald-600 text-white hover:bg-emerald-500"
                                                    data-testid="sign-off-button"
                                                >
                                                    <FileSignature className="h-4 w-4 mr-2" />
                                                    Sign Off
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    {/* Progress */}
                                    <div className="mb-6 p-4 bg-slate-800/50 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-slate-400">Progress</span>
                                            <span className="text-sm font-medium text-slate-200">
                                                {selectedEntry.items.filter(i => i.is_completed).length} of {selectedEntry.items.length} completed
                                            </span>
                                        </div>
                                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full transition-all duration-300"
                                                style={{ 
                                                    backgroundColor: "var(--brand-accent)",
                                                    width: `${(selectedEntry.items.filter(i => i.is_completed).length / selectedEntry.items.length) * 100}%` 
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Checklist Items */}
                                    <div className="space-y-3">
                                        {selectedEntry.items.map((item, idx) => (
                                            <div
                                                key={item.id}
                                                className={cn(
                                                    'checklist-item',
                                                    item.is_completed && 'completed'
                                                )}
                                                data-testid={`checklist-item-${item.id}`}
                                            >
                                                <Checkbox
                                                    id={item.id}
                                                    checked={item.is_completed}
                                                    onCheckedChange={(checked) => handleItemToggle(item.id, checked)}
                                                    disabled={selectedEntry.signed_off_by}
                                                    className="border-slate-600 data-[state=checked]:bg-[var(--brand-primary)] data-[state=checked]:border-[var(--brand-primary)]"
                                                />
                                                <div className="flex-1">
                                                    <label
                                                        htmlFor={item.id}
                                                        className={cn(
                                                            'text-sm cursor-pointer',
                                                            item.is_completed ? 'text-slate-500 line-through' : 'text-slate-200'
                                                        )}
                                                    >
                                                        {item.description}
                                                    </label>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <span className="text-xs text-slate-600 capitalize">{item.category}</span>
                                                        {item.is_required && (
                                                            <span className="text-xs text-amber-500">Required</span>
                                                        )}
                                                        {item.completed_at && (
                                                            <span className="text-xs text-emerald-500">
                                                                Completed by {item.completed_by} at {formatDateTime(item.completed_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Sign off info */}
                                    {selectedEntry.signed_off_by && (
                                        <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <FileSignature className="h-5 w-5 text-emerald-400" />
                                                <div>
                                                    <p className="text-sm font-medium text-emerald-400">
                                                        Signed off by {selectedEntry.signed_off_by}
                                                    </p>
                                                    <p className="text-xs text-emerald-400/70">
                                                        {formatDateTime(selectedEntry.signed_off_at)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="py-16">
                                    <div className="empty-state">
                                        <ClipboardCheck className="empty-state-icon" />
                                        <p className="empty-state-title">Select a checklist</p>
                                        <p className="empty-state-description">
                                            Choose a checklist from the list or create a new one
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DailyDiary;
